/**
 * The browser client's fetch, watching the two limits PostgREST applies
 * without saying so. It only reports, so a read behaves exactly as before:
 *
 * - **URL length.** An `.in()` list rides in the URL, and the gateway answers
 *   400 once a query passes ~25k characters (an account with 722 projects lost
 *   its project list to it). Past `WARN_URL_CHARS` the request is reported, so
 *   a read that grows with a workspace is found while it still works.
 * - **Row cap.** A response holds at most `DB_MAX_ROWS` rows and is trimmed
 *   silently. A read that set no limit and came back with exactly that many
 *   was almost certainly cut short; page it with `readAllPages`.
 *
 * Each finding is reported once per kind, method and table per page load.
 * The errors are Sentry's, never thrown, so no display site can render them.
 */

import { captureSentryError } from '@/lib/sentry'
import { DB_MAX_ROWS } from '@/lib/supabasePaging'

/** Half of what the gateway refuses. */
export const WARN_URL_CHARS = 12_000

export class SupabaseUrlLengthWarning extends Error {
  constructor(
    readonly table: string,
    readonly method: string,
    readonly urlLength: number,
  ) {
    super(`Supabase ${method} on ${table} has a request URL nearing the gateway's limit`)
    this.name = 'SupabaseUrlLengthWarning'
  }
}

export class SupabaseRowCapWarning extends Error {
  constructor(
    readonly table: string,
    readonly method: string,
    readonly rows: number,
  ) {
    super(`Supabase ${method} on ${table} returned the row cap with no limit, so it was probably cut short`)
    this.name = 'SupabaseRowCapWarning'
  }
}

type Finding = SupabaseUrlLengthWarning | SupabaseRowCapWarning
type Fetch = typeof fetch

function requestUrl(input: Parameters<Fetch>[0]): string {
  if (typeof input === 'string') return input
  if (input instanceof URL) return input.href
  return input.url
}

function requestMethod(input: Parameters<Fetch>[0], init: Parameters<Fetch>[1]): string {
  return (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase()
}

/** `/rest/v1/projects` → `projects`; any other path (auth, storage) as it is. */
function tableOf(url: URL): string {
  return url.pathname.startsWith('/rest/v1/') ? url.pathname.slice('/rest/v1/'.length) : url.pathname
}

/** Rows in a PostgREST `Content-Range` (`0-999/*`); null for none or an empty page. */
function rowsInRange(contentRange: string | null): number | null {
  const match = /^(\d+)-(\d+)\//.exec(contentRange ?? '')
  return match ? Number(match[2]) - Number(match[1]) + 1 : null
}

function reportToSentry(finding: Finding): void {
  captureSentryError(finding, {
    operation: finding instanceof SupabaseUrlLengthWarning ? 'supabase_url_length' : 'supabase_row_cap',
    level: 'warning',
    tags: { table: finding.table, method: finding.method },
    extra:
      finding instanceof SupabaseUrlLengthWarning
        ? { urlLength: finding.urlLength, warnUrlChars: WARN_URL_CHARS }
        : { rows: finding.rows, maxRows: DB_MAX_ROWS },
  })
}

/** `base` is read at call time, so a test's mock of `globalThis.fetch` still sees every request. */
export function guardSupabaseFetch(
  base: () => Fetch = () => globalThis.fetch,
  report: (finding: Finding) => void = reportToSentry,
): Fetch {
  const reported = new Set<string>()
  const reportOnce = (finding: Finding) => {
    const key = `${finding.name} ${finding.method} ${finding.table}`
    if (reported.has(key)) return
    reported.add(key)
    report(finding)
  }
  return async (input, init) => {
    const href = requestUrl(input)
    const method = requestMethod(input, init)
    const url = new URL(href, 'http://localhost')
    if (href.length > WARN_URL_CHARS) reportOnce(new SupabaseUrlLengthWarning(tableOf(url), method, href.length))
    const response = await base()(input, init)
    const unbounded = !url.searchParams.has('limit') && !new Headers(init?.headers).has('range')
    if (method === 'GET' && unbounded) {
      const rows = rowsInRange(response.headers.get('content-range'))
      if (rows !== null && rows >= DB_MAX_ROWS) reportOnce(new SupabaseRowCapWarning(tableOf(url), method, rows))
    }
    return response
  }
}
